import React, { useState } from "react";
import { cn } from "@/lib/utils";
import axios from "axios";
import SimpleReCaptcha from "@/components/ReCaptcha/SimpleReCaptcha";
import RecaptchaModal from "@/components/ReCaptcha/RecaptchaModal";

interface ContactUsProps {
  data: ContactUsData;
  captchaMode?: "inline" | "modal";
}

export interface ContactUsData {
  sectionName: string;
  title: string;
}

const validateEmail = (email: string) => {
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return false;
  const parts = email.split("@");
  if (parts.length !== 2) return false;
  const domain = parts[1];
  if (!domain.includes(".")) return false;
  const tld = domain.split(".").pop();
  if (!tld || tld.length < 2) return false;
  return true;
};

const validatePhone = (phone: string) =>
  /^\+?\d{9,15}$/.test(phone.replace(/\s+/g, ""));

const getSourcePage = () =>
  typeof window !== "undefined"
    ? `${window.location.pathname}${window.location.search}`
    : "";

export default function ContactUs({
  data,
  captchaMode = "modal",
}: ContactUsProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "success" | "error"
  >("idle");
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [marketingError, setMarketingError] = useState("");
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [recaptchaError, setRecaptchaError] = useState("");
  const [isRecaptchaModalOpen, setIsRecaptchaModalOpen] = useState(false);
  const [pendingPayload, setPendingPayload] = useState<Record<string, unknown> | null>(null);
  const [pendingForm, setPendingForm] = useState<HTMLFormElement | null>(null);

  const submitForm = async (
    payload: Record<string, unknown>,
    formElement: HTMLFormElement
  ) => {
    try {
      const response = await axios.post("/api/send-interest", payload);
      if (response.data.success) {
        setSubmitStatus("success");
        formElement.reset();
        setRecaptchaToken("");
      } else {
        setSubmitStatus("error");
      }
    } catch (error) {
      console.error("Form submission error:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus("idle");
    setRecaptchaError("");

    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;
    const phone = formData.get("phone") as string;
    const marketingConsent = formData.get("marketing") === "on";

    let valid = true;

    if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address.");
      valid = false;
    } else {
      setEmailError("");
    }

    if (!validatePhone(phone)) {
      setPhoneError("Please enter a valid phone number.");
      valid = false;
    } else {
      setPhoneError("");
    }

    if (!marketingConsent) {
      setMarketingError("Please check the consent box to submit the form.");
      valid = false;
    } else {
      setMarketingError("");
    }

    if (!valid) {
      setIsSubmitting(false);
      return;
    }

    const dataToSend = {
      firstName: formData.get("firstName") as string,
      lastName: formData.get("lastName") as string,
      email,
      phone,
      message: formData.get("message") as string,
      marketing: marketingConsent,
      recaptchaToken,
      formName: "Home Top Contact Form",
      sourcePage: getSourcePage(),
    };

    if (captchaMode === "inline") {
      if (!recaptchaToken) {
        setRecaptchaError("Please complete reCAPTCHA before submitting.");
        setIsSubmitting(false);
        return;
      }

      await submitForm(dataToSend, e.currentTarget);
      return;
    }

    setPendingPayload(dataToSend);
    setPendingForm(e.currentTarget);
    setIsRecaptchaModalOpen(true);
    setIsSubmitting(false);
  };

  const handleInlineVerify = (token: string) => {
    setRecaptchaToken(token);
    setRecaptchaError("");
  };

  const handleInlineExpired = () => {
    setRecaptchaToken("");
  };

  const handleRecaptchaError = (message: string) => {
    setRecaptchaToken("");
    setRecaptchaError(message);
  };

  const handleModalClose = () => {
    setIsRecaptchaModalOpen(false);
    setPendingPayload(null);
    setPendingForm(null);
  };

  const handleModalVerify = async (token: string) => {
    if (!pendingPayload || !pendingForm) {
      setRecaptchaError("Unable to continue submit. Please try again.");
      setIsRecaptchaModalOpen(false);
      return;
    }

    setRecaptchaToken(token);
    setRecaptchaError("");
    setIsRecaptchaModalOpen(false);
    setIsSubmitting(true);

    await submitForm(
      {
        ...pendingPayload,
        recaptchaToken: token,
      },
      pendingForm
    );

    setPendingPayload(null);
    setPendingForm(null);
  };

  const handleOpenForm = () => {
    setSubmitStatus("idle");
    setEmailError("");
    setPhoneError("");
    setMarketingError("");
    setRecaptchaError("");
  };

  return (
    <div className="relative w-full bg-[#22344b] transition-all duration-500">
      <section
        className={cn(
          "relative w-full",
          "md:py-[27px] md:py-6 desktop:py-8",
          "max-w-[85vw] mx-auto mx-[7.5vw]"
        )}
      >
        <div className="flex lg:gap-8 w-full max-lg:flex-wrap max-lg:justify-center">
          <button
            type="button"
            onClick={() => {
              setIsFormVisible(!isFormVisible);
              if (!isFormVisible) {
                handleOpenForm();
              }
            }}
            aria-expanded={isFormVisible}
            className="flex whitespace-nowrap items-center gap-2 bg-transparent border-0 p-0 text-left cursor-pointer max-lg:w-full max-lg:justify-between max-lg:py-2 max-md:py-[27px]"
          >
            <h2 className="font-serif text-accent-color-two text-h2-mobile text-left leading-9 tracking-wide text-[#e4a33f]">
              {data.title}
            </h2>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="24"
              fill="none"
              className={cn(
                "h-6 w-6 shrink-0 transition-transform duration-200 text-accent-color-two lg:hidden",
                isFormVisible ? "rotate-180" : "rotate-0"
              )}
            >
              <path
                fill="currentColor"
                d="M12.148 23.23c-.36.36-1.019.36-1.378 0l-9.583-9.583c-.36-.36-.36-1.018 0-1.377.359-.36 1.018-.36 1.377 0l8.925 8.924 8.864-8.924c.36-.36 1.018-.36 1.378 0 .36.359.36 1.018 0 1.377zM21.73 2.148l-9.583 9.583c-.36.36-1.019.36-1.378 0L1.187 2.147c-.36-.36-.36-1.018 0-1.377.359-.36 1.018-.36 1.377 0l8.925 8.924L20.353.77c.36-.36 1.018-.36 1.378 0 .36.359.36 1.018 0 1.377"
              />
            </svg>
          </button>
          <div
            className={`w-full transition-all duration-300 ease-in-out ${
              isFormVisible
                ? "max-h-[1000px] opacity-100"
                : "max-lg:max-h-0 max-lg:opacity-0 max-lg:overflow-hidden lg:block"
            }`}
          >
            {submitStatus === "success" && (
              <div className="mb-6 p-4 rounded border border-white/40 bg-[#2a415b] text-accent-color-two">
                Thank you! Your message has been sent successfully.
              </div>
            )}
            {submitStatus === "error" && (
              <div className="mb-6 p-4 rounded border border-red-300/50 text-red-200">
                Sorry, there was an error sending your message. Please try
                again.
              </div>
            )}
            <div>
              <form
                id="home-top-contact-form"
                name="Home Top Contact Form"
                method="post"
                action="/api/send-interest"
                className="w-full relative pb-[1.5625rem] max-lg:pt-7"
                onSubmit={handleSubmit}
              >
                <div className="flex flex-col lg:flex-row gap-4">
                  <input type="hidden" name="city" />
                  <input type="hidden" name="recaptchaToken" value={recaptchaToken} readOnly />
                  <input
                    type="text"
                    name="firstName"
                    placeholder="First Name *"
                    className="w-full lg:w-[12%] bg-transparent text-neutral-50 text-sm font-brandon leading-normal max-lg:leading-[2.5] placeholder-neutral-50 focus:outline-none border-b border-neutral-50"
                    aria-required="true"
                    required
                  />
                  <input
                    type="text"
                    name="lastName"
                    placeholder="Last Name *"
                    className="w-full lg:w-[12%] bg-transparent text-neutral-50 text-sm font-brandon leading-normal max-lg:leading-[2.5] placeholder-neutral-50 focus:outline-none border-b border-neutral-50"
                    aria-required="true"
                    required
                  />
                  <input
                    type="tel"
                    name="phone"
                    placeholder="Phone Number *"
                    className="w-full lg:w-[12%] bg-transparent text-neutral-50 text-sm font-brandon leading-normal max-lg:leading-[2.5] placeholder-neutral-50 focus:outline-none border-b border-neutral-50"
                    aria-required="true"
                    required
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="E-mail Address *"
                    className="w-full lg:w-[12%] bg-transparent text-neutral-50 text-sm font-brandon leading-normal max-lg:leading-[2.5] placeholder-neutral-50 focus:outline-none border-b border-neutral-50"
                    aria-required="true"
                    required
                  />
                  <input
                    type="text"
                    name="message"
                    placeholder="Message *"
                    className="w-full lg:w-[40%] bg-transparent text-neutral-50 text-sm font-brandon leading-normal max-lg:leading-[2.5] placeholder-neutral-50 focus:outline-none border-b border-neutral-50"
                    aria-required="true"
                    required
                  />
                  <div className="flex justify-between items-center max-md:mb-4 mt-4 lg:absolute lg:bottom-0 lg:right-0">
                    <div className="flex-1" />
                    <div className="flex flex-col gap-1 lg:mr-[6.8rem]">
                      <label className="flex items-center gap-2 max-lg:items-start">
                        <div className="relative w-3 h-3 border border-neutral-50 max-lg:w-4 max-lg:h-4 max-lg:shrink-0 max-lg:mt-0.5 max-lg:rounded-[2px]">
                          <input
                            type="checkbox"
                            name="marketing"
                            className="absolute inset-0 w-full h-full cursor-pointer accent-neutral-50 opacity-100"
                            aria-required="true"
                            aria-invalid={marketingError ? "true" : "false"}
                          />
                        </div>
                        <span className="text-neutral-50 text-xs font-brandon leading-tight">
                          I consent to receive phone calls, text messages, and
                          emails from Solomon Facial Plastic Surgery.
                        </span>
                      </label>
                      {marketingError && (
                        <span className="text-red-400 text-xs font-brandon md:absolute top-[20px] text-[#e4a33f]">
                          {marketingError}
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="relative lg:top-[0.625rem] lg:ml-4 px-6 py-2.5 bg-transparent border border-neutral-50 rounded text-neutral-50 text-base font-brandon leading-7 tracking-[0.5px] hover:bg-neutral-50/10 transition-colors disabled:opacity-50 whitespace-nowrap"
                  >
                    {isSubmitting ? "Submitting..." : "Send"}
                  </button>
                </div>
                {captchaMode === "inline" ? (
                  <div className="mt-4">
                    <SimpleReCaptcha
                      onVerify={handleInlineVerify}
                      onExpired={handleInlineExpired}
                      onError={handleRecaptchaError}
                    />
                  </div>
                ) : null}
                {(phoneError || emailError || recaptchaError) && (
                  <div className="mt-3 flex flex-col gap-1">
                    {phoneError && (
                      <span className="text-red-400 text-xs font-brandon">
                        {phoneError}
                      </span>
                    )}
                    {emailError && (
                      <span className="text-red-400 text-xs font-brandon">
                        {emailError}
                      </span>
                    )}
                    {recaptchaError && (
                      <span className="text-red-400 text-xs font-brandon">
                        {recaptchaError}
                      </span>
                    )}
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>
      <RecaptchaModal
        isOpen={captchaMode === "modal" && isRecaptchaModalOpen}
        onClose={handleModalClose}
        onVerify={handleModalVerify}
        onError={handleRecaptchaError}
        title="Verify you are human"
      />
    </div>
  );
}
